import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { BrandMark } from "@/components/BrandMark";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const MISMATCH = "Your passwords don't match. Please try again.";
const BAD = {
  expired: "This password reset link has expired.",
  used: "This password reset link has already been used.",
  invalid: "This password reset link isn't valid.",
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const invite = params.get("invite") === "1";
  const [state, setState] = useState("checking");
  const [pw, setPw] = useState({ a: "", b: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return setState("invalid");
    api.get("/auth/reset-password/check", { params: { token } })
      .then(() => setState("ok"))
      .catch((e) => setState(BAD[e?.response?.data?.detail] ? e.response.data.detail : "invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setError(MISMATCH);
    setLoading(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw.a, confirm_password: pw.b });
      logout();
      navigate("/signin?mode=login&reset=1", { replace: true });
    } catch (err) {
      const msg = apiError(err);
      if (/expired|already been used|no longer valid/i.test(msg)) setState("expired");
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="glass-card p-7 sm:p-9 fade-up" data-testid="reset-card">
          <div className="flex justify-center mb-5"><BrandMark size={56} /></div>
          {state === "checking" && <p className="text-center text-[#B9B3C8]" data-testid="reset-checking">Checking your link…</p>}
          {BAD[state] && (
            <div className="text-center" data-testid="reset-invalid">
              <h1 className="font-serif text-3xl text-[#EDE6F7] mb-3">{BAD[state]}</h1>
              <p className="text-lg text-[#B9B3C8] mb-7">Reset links work once and expire after 1 hour. You can request a fresh one now.</p>
              <Link to="/forgot-password" data-testid="reset-request-new" className="gold-btn inline-flex items-center justify-center w-full h-16 rounded-full text-xl">Request a new link</Link>
            </div>
          )}
          {state === "ok" && (
            <>
              <h1 className="font-serif text-3xl sm:text-4xl text-center text-[#EDE6F7] mb-3" data-testid="reset-heading">{invite ? "Set your password" : "Choose a new password"}</h1>
              <p className="text-center text-[#B9B3C8] mb-7">At least 8 characters, including a letter and a number.</p>
              <form onSubmit={submit} className="space-y-5">
                <PasswordInput label="New password" name="new_password" testid="reset-password" autoComplete="new-password" value={pw.a} onChange={(e) => { setPw({ ...pw, a: e.target.value }); setError(""); }} />
                <PasswordInput label="Confirm new password" name="confirm_new_password" testid="reset-confirm" autoComplete="new-password" value={pw.b} onChange={(e) => { setPw({ ...pw, b: e.target.value }); setError(""); }} error={error === MISMATCH} />
                {error && <p role="alert" className="rounded-xl border border-[#6B2A4A] bg-[#1E1428] px-4 py-3 text-[#E6CCF2]" data-testid="reset-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="reset-submit" className="gold-btn w-full h-16 rounded-full text-xl disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EDE6F7]">
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
